/**
 * Service class for sending tracking events to Hurra.
 * Handles add-to-cart and add-to-wishlist events.
 */
class HurraEventService {
    /**
     * Pushes a tracking event to Hurra manually (when Hurra script is already loaded).
     *
     * @param {Object} owaParams - The OWA parameters object
     */
    static pushTrackingEvent(owaParams = null) {
        const params = owaParams ?? window.owaParams;
        if (!params) return;

        window.hc_tm = window.hc_tm ?? [];
        window.hc_tm.push({e: 'track', p: params});
    }

    /**
     * Sends an add-to-cart event to Hurra.
     *
     * @param {Object} options - Event options
     * @param {string} options.eventId - The event ID
     * @param {number} options.price - The product price
     * @param {Object} [options.owaParams] - Current OWA parameters (defaults to window.owaParams)
     * @returns {boolean} True if event was sent, false if prerequisites not met
     */
    static sendAddToCartEvent({eventId, price, owaParams}) {
        const params = owaParams ?? window.owaParams;
        if (!params) return false;

        // Create a copy with cart-specific parameters to avoid mutation
        const cartParams = {
            ...params,
            page_type: 'add_to_cart',
            product_quantity: 1,
            eventId,
            product_price: price
        };

        // Push event
        this.pushTrackingEvent(cartParams);

        return true;
    }

    /**
     * Sends an add-to-wishlist event to Hurra.
     *
     * @param {Object} options - Event options
     * @param {string} options.eventId - The event ID
     * @param {Object} [options.owaParams] - Current OWA parameters (defaults to window.owaParams)
     * @returns {boolean} True if event was sent, false if prerequisites not met
     */
    static sendAddToWishlistEvent({eventId, owaParams}) {
        const params = owaParams ?? window.owaParams;
        if (!params) return false;

        // Create a copy with wishlist-specific parameters to avoid mutation
        const wishlistParams = {
            ...params,
            page_type: 'add_to_wishlist',
            product_quantity: 1,
            eventId
        };

        // Push event
        this.pushTrackingEvent(wishlistParams);

        return true;
    }

    /**
     * Subscribes to MessageBroker events for cart and wishlist actions.
     * Returns unsubscribe function to clean up subscriptions.
     *
     * @param {Object} MessageBroker - The MessageBroker instance
     * @param {Object} Messages - The Messages constants object
     * @returns {Function} Unsubscribe function to remove all subscriptions
     */
    static subscribeToEvents(MessageBroker, Messages) {
        const subscriptions = [
            MessageBroker.subscribe(Messages.CART.ITEM_ADD_SUCCESS, ({eventId, price}) => {
                this.sendAddToCartEvent({eventId, price});
            }),
            MessageBroker.subscribe(Messages.WISHLIST.ITEM_ADDED, ({eventId}) => {
                this.sendAddToWishlistEvent({eventId});
            })
        ];

        // Return unsubscribe function
        return () => {
            subscriptions.forEach(sub => MessageBroker.unsubscribe(sub));
        };
    }
}

export default HurraEventService;
